import { eventShortNameOptions, generateCreativeName, languageOptions, PAGE_ID, PAGE_TYPE, PLATFORMS } from '@amzn/journeys-common';
import { Row } from '@amzn/meridian';
import { intentOptions, productLineOptions, reasonOptions } from 'app.constants';
import type { DropdownOptions } from 'app.types';
import { required } from 'components/form/form.util';
import { MFormToggle } from 'components/MeridianFinalFormWrappers/MFormToggle';
import { forEach } from 'es-toolkit/compat';
import { type FC, useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';
import { JFormInput } from '../../../components/form/JFormInput';
import { JFormRadioGroup } from '../../../components/form/JFormRadioGroup';
import { JFormSelect } from '../../../components/form/JFormSelect';
import { JFormTagMultiselect } from '../../../components/form/JFormTagMultiselect';
import { alexaEnabledOptions, type CampaignFormValues, REQUIRED, SchedulingLocation } from '../campaign.types';

export type CampaignDetailsProps = {
  enableAutoNaming: boolean;
  hasPlatform: boolean;
  hasPageId: boolean;
  isGatewayCampaign: boolean;
  platformOptions: DropdownOptions[];
};

export const CampaignDetails: FC<CampaignDetailsProps> = (props) => {
  const form = useForm<CampaignFormValues>();
  const campaign = form.getState().values.campaign;

  const updateCreativeName = useCallback(
    (index: number, value: string) => {
      const creativeNameParams = [
        campaign.eventShortName ?? '',
        campaign.program ?? '',
        campaign.alexaEnabled ?? '',
        campaign.additionalName ?? '',
      ];
      creativeNameParams[index] = value;
      const combinedCreativeName = generateCreativeName({
        additionalName: creativeNameParams[3],
        alexaEnabled: creativeNameParams[2] as 'Y' | 'N',
        eventShortName: creativeNameParams[0],
        program: creativeNameParams[1],
      });
      form.change(
        'campaign.title' as keyof CampaignFormValues,
        combinedCreativeName as unknown as CampaignFormValues[keyof CampaignFormValues],
      );
    },
    [
      campaign.additionalName,
      campaign.alexaEnabled,
      campaign.eventShortName,
      campaign.program,
      form,
    ],
  );

  // update the title field according to 4 fields
  const alexaEnabledChanged = useCallback(
    (value: string) => {
      updateCreativeName(2, value);
    },
    [
      updateCreativeName,
    ],
  );
  const programChanged = useCallback(
    (value: string) => {
      updateCreativeName(1, value);
    },
    [
      updateCreativeName,
    ],
  );
  const eventShortNameChanged = useCallback(
    (value: string) => {
      updateCreativeName(0, value);
    },
    [
      updateCreativeName,
    ],
  );
  const additionalNameChanged = useCallback(
    (value: string) => {
      updateCreativeName(3, value);
    },
    [
      updateCreativeName,
    ],
  );

  const platformChanged = (platform: string) => {
    forEach(campaign.placements, (placement, index) => {
      form.change(
        `campaign.placements[${index}].platform` as unknown as keyof CampaignFormValues,
        platform as unknown as CampaignFormValues[keyof CampaignFormValues],
      );
      form.change(
        `campaign.placements[${index}].pageId` as unknown as keyof CampaignFormValues,
        PAGE_ID[platform as keyof typeof PAGE_ID] as unknown as CampaignFormValues[keyof CampaignFormValues],
      );
      let pageTypeValue: PAGE_TYPE | undefined;
      switch (platform) {
        case PLATFORMS.DESKTOP:
          pageTypeValue = PAGE_TYPE.DETAIL;
          break;
        case PLATFORMS.MOBILE:
        case PLATFORMS.TABLET:
          pageTypeValue = PAGE_TYPE.DETAILAW;
          break;
      }
      if (props.isGatewayCampaign) {
        pageTypeValue = PAGE_TYPE.GATEWAY;
      } else if (placement.scheduleType === SchedulingLocation.CATEGORY_PAGE) {
        pageTypeValue = platform === PLATFORMS.MOBILE ? PAGE_TYPE.LANDINGAW : PAGE_TYPE.BROWSE;
      }
      if (pageTypeValue) {
        form.change(
          `campaign.placements[${index}].pageType` as unknown as keyof CampaignFormValues,
          pageTypeValue as unknown as CampaignFormValues[keyof CampaignFormValues],
        );
      }
    });
  };

  const languageOptionsMemo = useMemo(() => languageOptions.sort((a, b) => (a.display > b.display ? 1 : -1)), []);

  const alexaEnabledRequired = (value: string, allValues: CampaignFormValues) => {
    if (allValues.campaign.isAutoGenerateCreativeName && !value) {
      // if toggle on & no value then it shows required else not
      return REQUIRED;
    }
  };

  const businessGroupDescription = () => (
    <>
      Enter a custom &nbsp;
      <a href="https://console.harmony.a2z.com/symphony-group-manager-app/businessgroup" rel="noopener noreferrer" target="_blank">
        Business Group ID
      </a>
      &nbsp; or leave blank to use the default.
    </>
  );

  const accessControlGroupDescription = () => (
    <>
      Enter a custom &nbsp;
      <a href="https://console.harmony.a2z.com/symphony-group-manager-app/acg" rel="noopener noreferrer" target="_blank">
        Access Control Group ID
      </a>
      &nbsp; or leave blank to use the default.
    </>
  );

  return (
    <div>
      {!props.enableAutoNaming ? (
        <div className="form-row">
          <JFormInput
            autoComplete="off"
            className="col"
            disabled={campaign.isLocked ?? false}
            label="Creative Name"
            name="campaign.title"
            validate={required}
          />
        </div>
      ) : (
        <div className="auto-naming-section">
          <Row
            widths={[
              'fill',
              'fit',
            ]}
          >
            <JFormInput
              autoComplete="off"
              disabled={campaign.isLocked ?? false}
              label="Creative Name"
              name="campaign.title"
              readOnly={campaign.isAutoGenerateCreativeName ?? false}
              validate={required}
            />

            <MFormToggle
              disabled={campaign.isLocked ?? false}
              label="Automatically generate"
              name="campaign.isAutoGenerateCreativeName"
              size="small"
            />
          </Row>
          {campaign.isAutoGenerateCreativeName && (
            <div className="auto-naming-options-section">
              <div className="form-row">
                <JFormRadioGroup
                  className="col"
                  disabled={campaign.isLocked ?? false}
                  inline
                  label="Alexa-enabled device?"
                  name="campaign.alexaEnabled"
                  onChange={alexaEnabledChanged}
                  options={alexaEnabledOptions}
                  validate={alexaEnabledRequired}
                />
                <JFormInput
                  className="col"
                  description="Example: SATURN"
                  disabled={campaign.isLocked ?? false}
                  label="Program"
                  name="campaign.program"
                  onChange={programChanged}
                  validate={required}
                />
              </div>
              <div className="form-row">
                <JFormInput
                  className="col"
                  description="Example: VDAY"
                  disabled={campaign.isLocked ?? false}
                  label="Event short name"
                  list="events-list-id"
                  name="campaign.eventShortName"
                  onChange={eventShortNameChanged}
                  optional
                />
                <datalist id="events-list-id">
                  <option />
                  {eventShortNameOptions.map((eventShortName) => (
                    <option key={eventShortName.value}>{eventShortName.display}</option>
                  ))}
                </datalist>

                <JFormInput
                  autoComplete="off"
                  className="col"
                  description="This text will be added to the end of the name."
                  disabled={campaign.isLocked ?? false}
                  label="Additional name text"
                  name="campaign.additionalName"
                  onChange={additionalNameChanged}
                  optional
                />
              </div>
            </div>
          )}
        </div>
      )}

      <h4>Details</h4>
      <div className="form-row">
        {props.hasPlatform && (
          <JFormSelect
            className="col"
            disabled={campaign.isLocked ?? false}
            first=""
            label="Platform"
            name="campaign.placements[0].platform"
            onChange={(platform) => {
              platformChanged(platform);
            }}
            options={props.platformOptions}
            textField="display"
            validate={required}
            valueField="value"
          />
        )}

        <JFormSelect
          className="col"
          disabled={campaign.isLocked ?? false}
          first=""
          label="Marketplace/Content Language"
          name="campaign.language"
          options={languageOptionsMemo}
          textField="display"
          validate={required}
          valueField="display"
        />
      </div>

      <div className="form-row">
        <JFormSelect
          className="col"
          disabled={campaign.isLocked ?? false}
          first=""
          label="Product Line"
          name="campaign.productLine"
          options={productLineOptions}
          textField="display"
          validate={required}
          valueField="value"
        />

        <JFormTagMultiselect className="col" disabled={campaign.isLocked ?? false} label="Tags" name="campaign.tags" />
      </div>

      <div className="form-row">
        {props.hasPageId && (
          <JFormInput
            className="col"
            disabled={campaign.isLocked ?? false}
            label="Page ID"
            name="campaign.placements[0].pageId"
            validate={required}
          />
        )}
      </div>

      <div className="form-row">
        <JFormInput
          className="col"
          description={businessGroupDescription}
          disabled={campaign.isLocked ?? false}
          label="Custom Business Group ID"
          name="campaign.businessGroup"
          optional
        />

        <JFormInput
          className="col"
          description={accessControlGroupDescription}
          disabled={campaign.isLocked ?? false}
          label="Custom Access Control Group ID"
          name="campaign.accessControlGroup"
          optional
        />
      </div>

      <div className="form-row">
        <JFormSelect
          className="col"
          disabled={campaign.isLocked ?? false}
          first=""
          label="Reason"
          name="campaign.reason"
          options={reasonOptions}
          textField="display"
          validate={required}
          valueField="value"
        />
        <JFormSelect
          className="col"
          disabled={campaign.isLocked ?? false}
          first=""
          label="Marketing Type"
          name="campaign.intent"
          options={intentOptions}
          textField="display"
          validate={required}
          valueField="value"
        />
      </div>
    </div>
  );
};
